const CURRENT_IDENTITY_REF_PATH_POSITION = 2
const URL_IDENTITY_REF_PREFIX = '/i/'

export function getCookie(name: string) {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1]
}

export function getCsrfToken() {
  const testMetaTag = document.querySelector<HTMLMetaElement>('meta[name="test_env"]')
  if (testMetaTag?.content === 'true') return ''

  const metaTag = document.querySelector<HTMLMetaElement>('[name=csrf-token]')
  if (!metaTag) throw new Error('CSRF meta tag not found')

  return metaTag.content
}

export function getIdentityRefFromLocation() {
  if (!location.pathname.includes(URL_IDENTITY_REF_PREFIX)) {
    // Naive check, but we don't expect this to ever exist in another context
    return null
  }
  return location.pathname.split('/').at(CURRENT_IDENTITY_REF_PATH_POSITION)
}

export function randomId(prefix = 'random-id', suffix?: string) {
  if (!prefix && !suffix) {
    throw new Error('Provide either a prefix or suffix to `randomId()`')
  }

  // The JavaScript equivalent of Ruby's `SecureRandom.hex(4)`
  const uniqueId = Array.from(crypto.getRandomValues(new Uint8Array(4)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')

  return [prefix, uniqueId, suffix].filter(i => Boolean(i)).join('-')
}

export function resolveIdentityPrefixForPath(pathname) {
  if (pathname.includes(URL_IDENTITY_REF_PREFIX)) {
    return replaceIdentityPrefixFromPath(pathname)
  } else {
    return addIdentityPrefixToPath(pathname)
  }
}

export function replaceIdentityPrefixFromPath(pathname) {
  const currentIdentityRef = getIdentityRefFromLocation()
  if (!currentIdentityRef || !pathname.includes(URL_IDENTITY_REF_PREFIX)) return pathname

  const currentSegments = pathname.split('/')
  currentSegments[CURRENT_IDENTITY_REF_PATH_POSITION] = currentIdentityRef
  return currentSegments.join('/')
}

export function addIdentityPrefixToPath(pathname) {
  const currentIdentityRef = getIdentityRefFromLocation()
  if (!currentIdentityRef || pathname.includes(URL_IDENTITY_REF_PREFIX)) return pathname

  const currentSegments = pathname.split('/')
  currentSegments.splice(CURRENT_IDENTITY_REF_PATH_POSITION - 1, 0, 'i', currentIdentityRef)

  return currentSegments.join('/')
}

// Acumulates `nameCounts` and generates a `originalFilename_n` if it's not the first
export function generateUniqueFilename(originalFilename: string, nameCounts: Record<string, number> = {}): string {
  nameCounts[originalFilename] = (nameCounts[originalFilename] || 0) + 1

  if (nameCounts[originalFilename] === 1) return originalFilename

  const nameparts = originalFilename.split('.')
  let format = ''
  if (nameparts.length > 1) {
    format = nameparts.pop()
  }
  return `${nameparts.join('.')}_${nameCounts[originalFilename]}${format ? `.${format}` : ''}`
}

/**
 * Helper function to format seconds into a human-readable string (e.g., "1h 3m 12s", "5m 22s", or "45s")
 * @param {number} seconds - The number of seconds to format.
 * @returns {string} The formatted time string.
 */
export function formatTimeReport(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return 'Calculating...'
  }

  // Ensure we are dealing with whole seconds
  seconds = Math.floor(seconds)

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  let parts = []

  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0 || (hours === 0 && remainingSeconds === 0)) {
    // Always show minutes if no hours, unless 0 total time
    parts.push(`${minutes}m`)
  }
  if (remainingSeconds > 0) {
    parts.push(`${remainingSeconds}s`)
  }

  if (parts.length === 0) {
    return 'Done' // Handle the edge case of 0 seconds left
  }

  return parts.join(' ')
}

/**
 * Calculates the estimated time remaining for a download and returns a formatted string.
 *
 * @param {number} totalSize - The total size of the file in bytes.
 * @param {number} bytesReceived - The current number of bytes downloaded.
 * @param {Date} startTime - The time the download started (Date object).
 * @returns {string} A human-readable string of the time remaining in the "1h 3m 12s" format.
 */
export function calculateTimeLeftReport(totalSize: number, bytesReceived: number, startTime: Date): string {
  const now = new Date()
  const timeElapsedInSeconds = (now.getTime() - startTime.getTime()) / 1000

  if (bytesReceived === 0 || timeElapsedInSeconds === 0) {
    return formatTimeReport(Infinity) // Returns "Calculating..."
  }

  const downloadRateBps = bytesReceived / timeElapsedInSeconds
  const remainingBytes = totalSize - bytesReceived
  const timeLeftInSeconds = remainingBytes / downloadRateBps

  return formatTimeReport(timeLeftInSeconds)
}
